import "./style.scss";

import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import { whiteButton } from "components/TitlesList";
import { useCompanySettings } from "hooks/useCompanySettings";
import { electronicFormatIBAN, isValidBIC, isValidIBAN } from "ibantools";
import { useEffect, useState } from "react";
import type { Control, FieldErrors } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ICompany } from "store/settingsStore";
import { fetchCompanyInfo, patchCompanyInfo } from "store/settingsStore";
import { useAppDispatch } from "store/store";
import type { BankingFieldKey, CountryFeatures } from "types/countryFeatures";

export const textFieldStyles = {
    "&.MuiTextField-root": {
        margin: "auto",
        width: "100%",
    },
    ".MuiFormHelperText-root": {
        color: "rgb(211, 47, 47)",
    },
};

export const labelStyles = {
    color: "#737373",
    width: "100%",
    justifyContent: "space-between",
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "18px",
    marginBottom: "0.5rem",
    ".MuiTypography-root": {
        fontSize: "12px",
    },
    "&.MuiInputLabel-root": {
        fontFamily: "Poppins",
        fontStyle: "normal",
        width: "100%",
    },
};

type BankingValidationRule = { length?: number; minLength?: number; maxLength?: number };

type CompanyFormData = Partial<Record<BankingFieldKey, string>> & {
    ribOwnerName: string;
    iban: string;
    swift: string;
    siret: string;
};

interface CompanyProps {
    close: (success: boolean) => void;
    company: ICompany;
}

const getPlaceholder = (
    fieldKey: BankingFieldKey,
    rules: Record<string, { length?: number; minLength?: number; maxLength?: number }> | undefined,
): string => {
    const rule = rules?.[fieldKey];
    if (!rule) return "";
    const len = rule.length ?? rule.maxLength ?? 0;
    return "0".repeat(len);
};

interface MetadataBankingFieldsProps {
    metadataFields: BankingFieldKey[];
    validationRules: CountryFeatures["validation"]["bankingFields"];
    control: Control<CompanyFormData>;
    errors: FieldErrors<CompanyFormData>;
}

const MetadataBankingFields = ({ metadataFields, validationRules, control, errors }: MetadataBankingFieldsProps) => {
    const [t] = useTranslation();

    const buildFieldValidator =
        (rule: BankingValidationRule) =>
        (v: string): boolean => {
            if (!v) return true;
            const clean = v.replace(/[\s-]/g, "");
            if (rule.length != null) return new RegExp(`^\\d{${rule.length}}$`).test(clean);
            if (rule.minLength != null) return new RegExp(`^\\d{${rule.minLength},${rule.maxLength}}$`).test(clean);
            return true;
        };

    const buildValidateMessage = (rule: BankingValidationRule, fieldKey: BankingFieldKey): string =>
        rule.length != null
            ? t("Settings.BankFieldExactLength", { field: t(`Beneficiary.${fieldKey}`), count: rule.length })
            : t("Settings.BankFieldLengthRange", {
                  field: t(`Beneficiary.${fieldKey}`),
                  min: rule.minLength,
                  max: rule.maxLength,
              });

    return (
        <>
            {metadataFields.map((fieldKey) => {
                const rule = validationRules?.[fieldKey as keyof typeof validationRules] as
                    | BankingValidationRule
                    | undefined;
                const validate = rule ? buildFieldValidator(rule) : undefined;
                const validateMsg = rule ? buildValidateMessage(rule, fieldKey) : undefined;

                return (
                    <TextFormComponent
                        key={fieldKey}
                        label={t(`Beneficiary.${fieldKey}`)}
                        control={control}
                        controlName={fieldKey as string}
                        placeholder={getPlaceholder(fieldKey, validationRules as any)}
                        required
                        customRequiredMessage={t("IbanModal.RequiredField")}
                        error={errors[fieldKey] as any}
                        customValidate={validate}
                        customValidateMessage={validateMsg}
                    />
                );
            })}
        </>
    );
};

const CompanyModal = ({ close, company }: CompanyProps) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { countryFeatures } = useCompanySettings();
    const [showMessageIBAN, setShowMessageIBAN] = useState(false);
    const [showMessageSwift, setShowMessageSwift] = useState(false);

    const usesIban = countryFeatures.bankingFields.includes("iban");
    const metadataFields: BankingFieldKey[] = [
        ...countryFeatures.bankingFields.filter((f): f is BankingFieldKey => f !== "iban" && f !== "bic"),
        ...(countryFeatures.companyBankingFields ?? []),
    ];

    const meta = company.bankingMetadata as Record<string, string> | null;

    const defaultValues: CompanyFormData = {
        ribOwnerName: company.bankAccountName ?? "",
        iban: company.iban ?? "",
        swift: company.swift ?? "",
        siret: company.siret ?? "",
        ...Object.fromEntries(metadataFields.map((f) => [f, meta?.[f] ?? ""])),
    };

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<CompanyFormData>({ defaultValues });

    const ibanValue = watch("iban");
    const swiftValue = watch("swift");

    const hasIbanInput = !!(ibanValue || swiftValue);
    const hasIbanFormatWarning = usesIban && hasIbanInput && (showMessageIBAN || showMessageSwift);

    useEffect(() => {
        if (!isValidIBAN(ibanValue ?? "") && ibanValue !== "") setShowMessageIBAN(true);
        if (!isValidBIC(swiftValue ?? "") && swiftValue !== "") setShowMessageSwift(true);
    }, [ibanValue, swiftValue]);

    const onSubmit = (data: CompanyFormData) => {
        const bankPayload = usesIban
            ? { bankAccountName: data.ribOwnerName, iban: data.iban, swift: data.swift }
            : {
                  bankingMetadata: Object.fromEntries(
                      metadataFields.map((f) => [f, data[f] ?? ""]).filter(([, v]) => v !== ""),
                  ),
              };
        dispatch(patchCompanyInfo({ ...company, ...bankPayload, siret: data.siret })).then(() =>
            dispatch(fetchCompanyInfo()),
        );
        close(true);
    };

    return (
        <div className="company-iban-container">
            <form className="bank-form" onSubmit={handleSubmit(onSubmit)}>
                {usesIban ? (
                    <>
                        <TextFormComponent
                            label={t("IbanModal.CompanyName")}
                            control={control}
                            controlName="ribOwnerName"
                            placeholder={t("IbanModal.CompanyExample")}
                            required
                            customRequiredMessage={t("IbanModal.RequiredField")}
                            error={errors.ribOwnerName}
                        />
                        <TextFormComponent
                            label={t("IbanModal.IBAN")}
                            control={control}
                            controlName="iban"
                            required
                            customRequiredMessage={t("IbanModal.RequiredField")}
                            error={errors.iban}
                            placeholder={"Ex: XXX XXX XXX XXX XXX XXX"}
                        />
                        <TextFormComponent
                            label={t("IbanModal.Swift")}
                            control={control}
                            controlName="swift"
                            placeholder={"Ex: SOGEFRPP"}
                        />
                    </>
                ) : (
                    <MetadataBankingFields
                        metadataFields={metadataFields}
                        validationRules={countryFeatures.validation.bankingFields}
                        control={control}
                        errors={errors}
                    />
                )}
                {!usesIban && (
                    <TextFormComponent
                        label={t("Beneficiars.Siret")}
                        control={control}
                        controlName="siret"
                        placeholder={""}
                        required
                        customRequiredMessage={t("IbanModal.RequiredField")}
                        error={errors.siret}
                    />
                )}
                {hasIbanFormatWarning && <MessageBlock type="warning" text={t("CompanyBankModal.FullErrors")} />}
                <div className="bank-modal-btns">
                    <StyledButton
                        type="button"
                        onClick={() => close(true)}
                        fullWidth
                        sx={{ ...whiteButton, color: "#ED6E6E" }}
                    >
                        {t("IbanModal.IbanFormCancel")}
                    </StyledButton>
                    <StyledButton buttonType="primary" fullWidth type="submit">
                        {t("IbanModal.IbanFormSave")}
                    </StyledButton>
                </div>
            </form>
        </div>
    );
};

export default CompanyModal;
